import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, Sparkles } from "lucide-react";
import { trackEvent } from "@/hooks/useBusinessData";

const CATEGORIES = [
  "All",
  "Haircuts",
  "Beard & Shave",
  "Color",
  "Twists",
  "Add-Ons",
];

export default function Services({ services, categoryNotes = {}, vip, config }) {
  const [cat, setCat] = useState("All");
  const navigate = useNavigate();
  const list = useMemo(
    () => (cat === "All" ? services : services.filter((s) => s.category === cat)),
    [cat, services]
  );

  const handleBook = (serviceId) => {
    trackEvent("book_clicked", { source: "services", service_id: serviceId });
    navigate("/book");
  };

  const note = cat !== "All" ? categoryNotes[cat] : null;

  return (
    <section
      id="services"
      className="relative bg-black text-white py-24 md:py-40 border-t border-white/10"
      data-testid="services-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20 items-end">
          <div className="col-span-12 md:col-span-6">
            <div className="eyebrow mb-6">§ The Menu</div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
              className="font-serif text-4xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight"
            >
              The Menu.
              <br />
              <span className="italic text-zinc-400">Precision meets peace.</span>
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:pl-8">
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              A complete cutting, beard, color, twist, and grooming menu — built on 25+ years of shear and clipper mastery, and delivered with genuine hospitality.
            </p>
          </div>
        </div>

        {/* Category tabs */}
        <div
          className="flex flex-wrap gap-2 md:gap-3 mb-6 md:mb-8"
          data-testid="services-tabs"
        >
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`service-tab-${c.toLowerCase().replace(/\s|&/g, "-")}`}
              className={`px-4 md:px-5 py-2 md:py-2.5 rounded-full border eyebrow text-[10px] md:text-[11px] transition-all ${
                cat === c
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-white border-white/25 hover:border-white/60"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {note && (
          <div className="mb-6 text-xs md:text-sm text-zinc-500 italic">
            {note}
          </div>
        )}

        {/* Service list */}
        <div className="border-t border-white/10">
          <AnimatePresence mode="popLayout">
            {list.map((s, i) => (
              <motion.article
                layout
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, delay: i * 0.03, ease: [0.7, 0, 0.2, 1] }}
                className="group grid grid-cols-12 gap-4 md:gap-6 items-center border-b border-white/10 py-6 md:py-8 hover:bg-white/[0.02] transition-colors"
                data-testid={`service-row-${s.id}`}
              >
                <div className="col-span-2 md:col-span-1 font-mono text-[10px] md:text-xs text-zinc-500">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-10 md:col-span-5">
                  <h3 className="font-serif text-xl md:text-3xl leading-tight">
                    {s.name}
                  </h3>
                  <div className="eyebrow text-zinc-500 mt-1 text-[10px]">
                    {s.category}
                  </div>
                </div>
                <div className="col-span-8 md:col-span-4 text-[13px] md:text-sm text-zinc-400 leading-relaxed">
                  {s.description}
                </div>
                <div className="col-span-4 md:col-span-2 flex items-center justify-end gap-3">
                  <div className="font-serif text-2xl md:text-3xl text-white whitespace-nowrap">
                    {s.price_display || `$${s.price}`}
                  </div>
                  <button
                    onClick={() => handleBook(s.id)}
                    data-testid={`service-book-${s.id}`}
                    className="p-3 rounded-full border border-white/25 hover:bg-white hover:text-black transition-colors"
                    aria-label={`Book ${s.name}`}
                  >
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.4} />
                  </button>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>

        <div className="mt-12 md:mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Link
            to="/book"
            onClick={() => trackEvent("book_clicked", { source: "services_footer" })}
            className="btn-primary"
            data-testid="services-book-cta"
          >
            Book an Appointment
          </Link>
          <a
            href={`tel:${config.phone_tel}`}
            onClick={() => trackEvent("call_clicked", { source: "services_footer" })}
            className="btn-ghost"
          >
            Or Call {config.phone}
          </a>
        </div>

        {/* VIP Membership */}
        {vip && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.7, 0, 0.2, 1] }}
            className="mt-24 md:mt-32 relative border border-white/15 bg-gradient-to-br from-white/[0.04] to-transparent p-8 md:p-14 overflow-hidden"
            data-testid="vip-membership"
            id="vip"
          >
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full border border-white/5 pointer-events-none" />
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full border border-white/5 pointer-events-none" />

            <div className="grid grid-cols-12 gap-6 md:gap-10 relative">
              <div className="col-span-12 md:col-span-6">
                <div className="eyebrow mb-4 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" strokeWidth={1.4} />
                  Membership
                </div>
                <h3 className="font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight">
                  She Cuts <span className="italic">VIP</span>.
                </h3>
                <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
                  For clients who value consistency, priority, and the full She Cuts ritual — every month.
                </p>

                <div className="mt-8 flex items-baseline gap-3">
                  <div className="font-serif text-6xl md:text-7xl">$250</div>
                  <div className="eyebrow text-zinc-400">/ month</div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href={`tel:${config.phone_tel}`}
                    onClick={() =>
                      trackEvent("vip_enroll_clicked", { channel: "call" })
                    }
                    className="btn-primary"
                    data-testid="vip-enroll-call"
                  >
                    Enroll — Call the Studio
                  </a>
                  <Link
                    to="/book"
                    onClick={() =>
                      trackEvent("vip_enroll_clicked", { channel: "book" })
                    }
                    className="btn-ghost"
                    data-testid="vip-enroll-book"
                  >
                    Book Consultation
                  </Link>
                </div>
              </div>

              <div className="col-span-12 md:col-span-6 md:pl-10 md:border-l border-white/10">
                <div className="eyebrow text-zinc-500 mb-6">Included Every Month</div>
                <ul className="space-y-4">
                  {vip.benefits.map((b, i) => (
                    <motion.li
                      key={b}
                      initial={{ opacity: 0, x: 10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08, duration: 0.5 }}
                      className="flex items-start gap-4 border-b border-white/5 pb-4"
                    >
                      <Check
                        className="w-5 h-5 mt-1 flex-none"
                        strokeWidth={1.4}
                      />
                      <span className="font-serif text-lg md:text-xl leading-snug">
                        {b}
                      </span>
                    </motion.li>
                  ))}
                </ul>
                <p className="mt-8 text-xs md:text-sm text-zinc-500 leading-relaxed max-w-md italic">
                  {vip.terms}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
